import { NATIVE_DELIVERY_TARGET } from "@hightouch/lib/llm/lifecycle-studio/esp-templating/dialects";

/**
 * A scope's email target is a destination id *or* the Native Delivery sentinel,
 * and the picker offers both in one list. These two functions are the whole
 * translation between that single picked value and the pair the mutations take.
 */
export function splitEspTargetSelection(selected?: string): {
  destinationId: string | null;
  targetKind: string | null;
} {
  if (!selected) return { destinationId: null, targetKind: null };
  return selected === NATIVE_DELIVERY_TARGET
    ? { destinationId: null, targetKind: NATIVE_DELIVERY_TARGET }
    : { destinationId: selected, targetKind: null };
}

export function espTargetSelection({
  destinationId,
  targetKind,
}: {
  destinationId?: string | number | null;
  targetKind?: string | null;
}): string {
  if (targetKind === NATIVE_DELIVERY_TARGET) return NATIVE_DELIVERY_TARGET;
  return String(destinationId ?? "");
}

export type EspTargetScope = {
  destinationId?: string | number | null;
  targetKind?: string | null;
};

/**
 * The target the nearest scope names, given scopes ordered most specific first
 * (campaign, brand, workspace default). Must keep mirroring
 * `resolveEmailEspContext` in `esp-templating/context.ts`, or this surface
 * reports a different provider — and syntax — than generation uses.
 */
export function effectiveEspTarget(scopes: EspTargetScope[]): string {
  for (const scope of scopes) {
    const selection = espTargetSelection(scope);
    if (selection) return selection;
  }
  return "";
}

export type EspDestinationRow = {
  id: string | number;
  name?: string | null;
  type: string;
};

export type EspScopeSelection = EspTargetScope & {
  /**
   * This scope's choice can't fall through when it names a destination we
   * can't resolve. Set on the campaign, where honoring an outer scope would
   * ground the email in a different ESP than the one deliberately chosen — a
   * brand or workspace miss keeps walking, as it does on the backend.
   */
  stopsOnUnresolved?: boolean;
};

export type EspScopeResolution = {
  /** The nearest scope that names anything named Native Delivery. */
  isNativeDelivery: boolean;
  /** The id the walk settled on, even where no row matches it. */
  destinationId: string | null;
  /** That id's row, when the destinations list holds it. */
  destination: EspDestinationRow | null;
};

/**
 * The same walk as `effectiveEspTarget`, resolved against the destinations the
 * workspace actually has. Matters where a scope names a destination that doesn't
 * resolve: `resolveEmailEspContext` stops there for a campaign and keeps walking
 * for a brand, and mirroring it keeps syntax help on generation's provider.
 */
export function resolveEspTarget({
  scopes,
  destinations,
}: {
  scopes: EspScopeSelection[];
  destinations: EspDestinationRow[];
}): EspScopeResolution {
  for (const scope of scopes) {
    if (scope.targetKind === NATIVE_DELIVERY_TARGET) {
      return {
        isNativeDelivery: true,
        destinationId: null,
        destination: null,
      };
    }
    const id =
      scope.destinationId == null ? "" : String(scope.destinationId).trim();
    if (!id) continue;

    const destination = destinations.find(
      (candidate) => String(candidate.id) === id,
    );
    if (destination) {
      return { isNativeDelivery: false, destinationId: id, destination };
    }
    if (scope.stopsOnUnresolved) {
      return { isNativeDelivery: false, destinationId: id, destination: null };
    }
  }
  return { isNativeDelivery: false, destinationId: null, destination: null };
}
